import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin, Send, CalendarCheck, FileDown } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";

const SERVICES = ["Microsoft Intune MDM", "Microsoft Defender", "Windows Autopilot", "Cloud Security", "General Consultation"];
const TIMES = ["09:00 ET", "11:00 ET", "13:00 ET", "15:00 ET", "17:00 ET"];

const inputCls =
  "w-full bg-white border border-black text-foreground text-sm px-4 py-3 placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors duration-200";

const emptyBooking = { name: "", email: "", company: "", service: SERVICES[0], preferred_date: "", preferred_time: TIMES[0], notes: "" };
const emptyEnquiry = { name: "", email: "", company: "", message: "" };

export default function Contact() {
  const { t: tr, lang } = useLang();
  const [mode, setMode] = useState("booking");
  const [booking, setBooking] = useState(emptyBooking);
  const [enquiry, setEnquiry] = useState(emptyEnquiry);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "booking") {
        await api.post("/bookings", { ...booking, lang });
        toast.success(tr("toast.booking"));
        setBooking(emptyBooking);
      } else {
        await api.post("/enquiries", { ...enquiry, lang });
        toast.success(tr("toast.enquiry"));
        setEnquiry(emptyEnquiry);
      }
    } catch (err) {
      toast.error(tr("toast.error"));
    } finally {
      setBusy(false);
    }
  };

  const set = (obj, setter) => (e) => setter({ ...obj, [e.target.name]: e.target.value });

  return (
    <section id="contact" data-testid="contact-section" className="py-24 sm:py-32 bg-[#F4F4F1] border-t border-black">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-primary mb-4">{tr("contact.overline")}</p>
          <h2 className="font-heading font-bold tracking-tight leading-none text-4xl sm:text-5xl lg:text-6xl mb-8">
            {tr("contact.h1a")}<br />{tr("contact.h1b")}<span className="text-accent">.</span>
          </h2>

          <div className="overflow-hidden border border-black mb-10 max-w-sm">
            <img
              src="/hamdou-portrait.jpeg"
              alt="Hamdou Wane, Principal Consultant"
              loading="lazy"
              className="w-full h-64 object-cover object-[30%_20%]"
            />
            <div className="p-4 bg-white">
              <p className="font-heading font-bold tracking-tight">Hamdou Wane</p>
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">{tr("contact.caption")}</p>
            </div>
          </div>

          <div className="space-y-4">
            <a href="mailto:contact@ztrustafrica.com" data-testid="contact-email-link" className="flex items-center gap-3 text-sm text-muted-foreground hover:text-accent transition-colors duration-300">
              <Mail className="w-4 h-4 text-primary" /> contact@ztrustafrica.com
            </a>
            <p className="flex items-center gap-3 text-sm text-muted-foreground">
              <MapPin className="w-4 h-4 text-primary" /> {tr("contact.location")}
            </p>
            <a href="/ztrust-services-en.pdf" target="_blank" rel="noopener noreferrer" data-testid="services-pdf-link" className="flex items-center gap-3 text-sm text-muted-foreground hover:text-accent transition-colors duration-300">
              <FileDown className="w-4 h-4 text-primary" /> {tr("contact.pdf")}
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="border border-black bg-white p-6 sm:p-10"
        >
          <div className="grid grid-cols-2 mb-8 border border-black" role="tablist" aria-label="Contact mode">
            <button
              type="button"
              role="tab"
              aria-selected={mode === "booking"}
              data-testid="tab-booking"
              onClick={() => setMode("booking")}
              className={`flex items-center justify-center gap-2 py-3.5 font-mono text-xs tracking-[0.15em] uppercase transition-colors duration-300 ${mode === "booking" ? "bg-primary text-background font-bold" : "text-muted-foreground hover:text-foreground"}`}
            >
              <CalendarCheck className="w-4 h-4" /> {tr("contact.tab1")}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "enquiry"}
              data-testid="tab-enquiry"
              onClick={() => setMode("enquiry")}
              className={`flex items-center justify-center gap-2 py-3.5 font-mono text-xs tracking-[0.15em] uppercase transition-colors duration-300 ${mode === "enquiry" ? "bg-primary text-background font-bold" : "text-muted-foreground hover:text-foreground"}`}
            >
              <Send className="w-4 h-4" /> {tr("contact.tab2")}
            </button>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid={mode === "booking" ? "booking-form" : "enquiry-form"}>
            {mode === "booking" ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input required name="name" data-testid="booking-name-input" placeholder={tr("contact.name")} value={booking.name} onChange={set(booking, setBooking)} className={inputCls} />
                  <input required type="email" name="email" data-testid="booking-email-input" placeholder={tr("contact.email")} value={booking.email} onChange={set(booking, setBooking)} className={inputCls} />
                </div>
                <input name="company" data-testid="booking-company-input" placeholder={tr("contact.company")} value={booking.company} onChange={set(booking, setBooking)} className={inputCls} />
                <div>
                  <label htmlFor="booking-service" className="block font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-2">{tr("contact.service")}</label>
                  <select id="booking-service" name="service" data-testid="booking-service-select" value={booking.service} onChange={set(booking, setBooking)} className={inputCls}>
                    {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="booking-date" className="block font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-2">{tr("contact.date")}</label>
                    <input required type="date" id="booking-date" name="preferred_date" data-testid="booking-date-input" value={booking.preferred_date} onChange={set(booking, setBooking)} className={`${inputCls}`} />
                  </div>
                  <div>
                    <label htmlFor="booking-time" className="block font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-2">{tr("contact.time")}</label>
                    <select id="booking-time" name="preferred_time" data-testid="booking-time-select" value={booking.preferred_time} onChange={set(booking, setBooking)} className={inputCls}>
                      {TIMES.map((slot) => <option key={slot} value={slot}>{slot}</option>)}
                    </select>
                  </div>
                </div>
                <textarea name="notes" data-testid="booking-notes-input" rows={3} placeholder={tr("contact.notes")} value={booking.notes} onChange={set(booking, setBooking)} className={`${inputCls} resize-none`} />
                <button type="submit" disabled={busy} data-testid="booking-submit-btn" className="w-full bg-primary text-background font-bold py-4 text-sm hover:bg-accent disabled:opacity-50 transition-colors duration-300">
                  {busy ? tr("contact.sending") : tr("contact.submitBooking")}
                </button>
              </>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input required name="name" data-testid="enquiry-name-input" placeholder={tr("contact.name")} value={enquiry.name} onChange={set(enquiry, setEnquiry)} className={inputCls} />
                  <input required type="email" name="email" data-testid="enquiry-email-input" placeholder={tr("contact.email")} value={enquiry.email} onChange={set(enquiry, setEnquiry)} className={inputCls} />
                </div>
                <input name="company" data-testid="enquiry-company-input" placeholder={tr("contact.company")} value={enquiry.company} onChange={set(enquiry, setEnquiry)} className={inputCls} />
                <textarea required name="message" data-testid="enquiry-message-input" rows={6} placeholder={tr("contact.message")} value={enquiry.message} onChange={set(enquiry, setEnquiry)} className={`${inputCls} resize-none`} />
                <button type="submit" disabled={busy} data-testid="enquiry-submit-btn" className="w-full bg-primary text-background font-bold py-4 text-sm hover:bg-accent disabled:opacity-50 transition-colors duration-300">
                  {busy ? tr("contact.sending") : tr("contact.submitEnquiry")}
                </button>
              </>
            )}
          </form>
        </motion.div>
      </div>
    </section>
  );
}
